// Liste des bons d'intervention (option « bons_intervention ») : filtres type / statut / site, recherche, création rapide.
// Option inactive : la page n'existe pas (rien n'est affiché de l'option).
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, FlaskConical, Plus, Search, Snowflake } from 'lucide-react';
import { LIBELLES_TYPE_BON, TYPES_BON, type TypeBon } from '@shared/bons-intervention.ts';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { useModulesEtat } from '../../lib/modules.ts';
import { date, pluriel } from '../../lib/format.ts';
import { Badge, Bouton, Chargement, Champ, ChampChoix, Dialogue, EntetePage, EtatErreur, EtatVide, Panneau, useNotifier } from '../../components/ui.tsx';
import { OngletsInterventions } from '../interventions/chantiers/commun.tsx';
import { PageIntrouvable } from '../legal/PageIntrouvable.tsx';
import type { Bon, BonResume } from './types.ts';

export function BadgeStatutBon({ b }: { b: Pick<BonResume, 'statut' | 'remplace'> }) {
  if (b.statut === 'signe' && b.remplace) return <Badge ton="neutre">Remplacé par un avenant</Badge>;
  return b.statut === 'signe' ? <Badge ton="succes">Signé</Badge> : <Badge ton="ambre">En cours, non signé</Badge>;
}

export function PageBons() {
  const { charge, modules } = useModulesEtat();
  const { peut } = useSession();
  if (!charge) return <Chargement />;
  if (!modules.bons_intervention || !peut('bons.lire')) return <PageIntrouvable />;
  return <ListeBons />;
}

function ListeBons() {
  const { peut } = useSession();
  const [params, setParams] = useSearchParams();
  const type = params.get('type') ?? '';
  const statut = params.get('statut') ?? '';
  const site = params.get('site') ?? '';
  const [texte, setTexte] = useState(params.get('q') ?? '');
  const [q, setQ] = useState(texte);
  const [creation, setCreation] = useState(false);
  useEffect(() => { const t = setTimeout(() => setQ(texte.trim()), 300); return () => clearTimeout(t); }, [texte]);
  const changer = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const sites = useQuery({ queryKey: ['sites'], queryFn: () => api.get<{ id: string; name: string }[]>('/api/sites') });
  const url = `/api/bons?${new URLSearchParams({ ...(type ? { type } : {}), ...(statut ? { statut } : {}), ...(site ? { site } : {}), ...(q ? { q } : {}) })}`;
  const liste = useQuery({ queryKey: ['bons', url], queryFn: () => api.get<BonResume[]>(url) });
  const filtre = !!(type || statut || site || q);

  return (
    <div className="pile-l">
      <EntetePage titre="Bons d’intervention" sousTitre="Ce qui a été fait sur place, signé sur le téléphone : passerelles, points communiqués, essais, sauvegardes, fluides."
        actions={<>
          {peut('bons.fluides') && <Link className="btn" to="/interventions/bons/fluides"><Snowflake aria-hidden />Fluides frigorigènes</Link>}
          {peut('bons.ecrire') && <Bouton variante="principal" icone={<Plus aria-hidden />} onClick={() => setCreation(true)}>Nouveau bon</Bouton>}
        </>} />
      <OngletsInterventions />
      <div className="int-filtres bi-filtres" role="search" aria-label="Filtrer les bons d’intervention">
        <label className="int-recherche">
          <Search size={18} aria-hidden className="discret" />
          <span className="visuellement-cache">Rechercher un bon</span>
          <input className="saisie" type="search" placeholder="Référence, titre, site, équipement" value={texte} onChange={(e) => setTexte(e.target.value)} />
        </label>
        <ChampChoix libelle="Type" value={type} onChange={(e) => changer('type', e.target.value)} vide="Tous les types"
          options={TYPES_BON.map((t) => ({ valeur: t, libelle: LIBELLES_TYPE_BON[t] }))} />
        <ChampChoix libelle="Statut" value={statut} onChange={(e) => changer('statut', e.target.value)} vide="Tous les statuts"
          options={[{ valeur: 'brouillon', libelle: 'En cours, non signés' }, { valeur: 'signe', libelle: 'Signés' }]} />
        <ChampChoix libelle="Site" value={site} onChange={(e) => changer('site', e.target.value)} vide="Tous les sites autorisés"
          options={(sites.data ?? []).map((s) => ({ valeur: s.id, libelle: s.name }))} />
      </div>
      {liste.isLoading && <Chargement />}
      {liste.isError && <EtatErreur erreur={liste.error} reessayer={() => liste.refetch()} />}
      {liste.data && (
        <Panneau serre titre={pluriel(liste.data.length, 'bon', 'bons')}>
          {liste.data.length === 0
            ? <EtatVide icone={<FileSignature />} titre={filtre ? 'Aucun bon ne correspond' : 'Aucun bon d’intervention'}
                texte={filtre ? 'Modifiez les filtres ou la recherche.' : 'Créez le bon sur place : type, site, puis remplissez-le et faites-le signer sur le téléphone.'}
                action={!filtre && peut('bons.ecrire') ? <Bouton variante="principal" onClick={() => setCreation(true)}>Créer un bon</Bouton> : undefined} />
            : (
              <div className="liste">
                {liste.data.map((b) => (
                  <Link key={b.id} to={`/interventions/bons/${b.id}`} className="liste-element bi-ligne-liste">
                    <span className="liste-element-corps">
                      <span className="liste-element-titre">{b.titre}</span>
                      <span className="petit discret">
                        <span className="mono">{b.reference}{b.version_no > 1 ? ` v${b.version_no}` : ''}</span> — {b.type_libelle} — {b.site_nom}{b.equipement ? ` — ${b.equipement}` : ''}
                      </span>
                    </span>
                    <span className="int-ligne-badges">
                      {b.demo && <Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démo</Badge>}
                      <BadgeStatutBon b={b} />
                    </span>
                    <span className="tres-petit discret expert-seulement masque-etroit">{b.statut === 'signe' ? `Signé le ${date(b.signe_le)}` : b.arrivee ? `Arrivée le ${date(b.arrivee)}` : ''}</span>
                  </Link>
                ))}
              </div>
            )}
        </Panneau>
      )}
      <DialogueCreation ouvert={creation} fermer={() => setCreation(false)} sites={sites.data ?? []} siteDefaut={site} />
    </div>
  );
}

function DialogueCreation({ ouvert, fermer, sites, siteDefaut }: { ouvert: boolean; fermer: () => void; sites: { id: string; name: string }[]; siteDefaut: string }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [type, setType] = useState<TypeBon | ''>('');
  const [siteId, setSiteId] = useState(siteDefaut);
  const [equipmentId, setEquipmentId] = useState('');
  const [demandeur, setDemandeur] = useState('');
  useEffect(() => { if (ouvert) { setSiteId(siteDefaut || (sites.length === 1 ? sites[0].id : '')); setEquipmentId(''); } }, [ouvert]);
  const liens = useQuery({ queryKey: ['bons-liens', siteId], queryFn: () => api.get<{ equipements: { id: string; label: string; reference: string | null }[] }>(`/api/bons/liens?site=${siteId}`), enabled: ouvert && !!siteId });
  const creer = useMutation({
    mutationFn: () => api.post<Bon>('/api/bons', { type, siteId, equipmentId: equipmentId || null, demandeur: demandeur || null }),
    onSuccess: (b) => { qc.invalidateQueries({ queryKey: ['bons'] }); notifier(`Bon ${b.reference} créé.`); fermer(); nav(`/interventions/bons/${b.id}`); },
    onError: (e) => notifier(messageErreur(e), true),
  });
  return (
    <Dialogue titre="Nouveau bon d’intervention" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton><Bouton variante="principal" chargement={creer.isPending} disabled={!type || !siteId} onClick={() => creer.mutate()}>Créer le bon</Bouton></>}>
      <div className="pile">
        <ChampChoix libelle="Type d’intervention" value={type} onChange={(e) => setType(e.target.value as TypeBon)} vide="Choisir…" required
          options={TYPES_BON.map((t) => ({ valeur: t, libelle: LIBELLES_TYPE_BON[t] }))} />
        <ChampChoix libelle="Site" value={siteId} onChange={(e) => { setSiteId(e.target.value); setEquipmentId(''); }} vide="Choisir…" required
          options={sites.map((s) => ({ valeur: s.id, libelle: s.name }))} />
        <ChampChoix libelle="Équipement (facultatif)" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)} vide="Aucun en particulier" disabled={!siteId}
          options={(liens.data?.equipements ?? []).map((x) => ({ valeur: x.id, libelle: `${x.label}${x.reference ? ` (${x.reference})` : ''}` }))} />
        <Champ libelle="Demandeur (facultatif)" value={demandeur} onChange={(e) => setDemandeur(e.target.value)} maxLength={300} placeholder="Nom, fonction" />
        <p className="tres-petit discret">L’heure d’arrivée est notée à la création ; vous êtes inscrit parmi les techniciens intervenus.</p>
      </div>
    </Dialogue>
  );
}
